import { Day } from 'date-fns';
import { Frequency, RRule } from 'shared/lib/rrule';
import {
  CompletionTargetUnit,
  FrequencyUnit,
  HabitSchedule,
  RruleReferenceDate,
  ScheduleFrequency,
} from 'shared/types/habit-schedule';
import { LimitKeys } from 'shared/types/limit-keys';
import { fullWeek, WeekDays } from 'shared/types/week-days';
import { getRruleWeekDays } from 'shared/utils/get-rrule-week-days';

export * as FilterDocuments from './filter-document';
export * as NLP from './nlp';

// can be found in `vite.config.ts`
export const version = __APP_VERSION__;

export const iosDownloadLink =
  'https://apps.apple.com/us/app/griply-control-your-life/id1556692747';

export const featurebaseRedirectUrl =
  'https://griply.featurebase.app/?b=69674e9d905acfac3f350533';

export const unlimitedLimit = -1;

export const defaultLimitKeyMap: Record<LimitKeys, number> = {
  [LimitKeys.CreateGoal]: 2,
  [LimitKeys.CreateHabit]: 2,
  [LimitKeys.HabitIcons]: 0,
  [LimitKeys.HabitScheduleTarget]: 0,
  [LimitKeys.CreateSmartList]: 3,
  [LimitKeys.GoalUserPhoto]: 0,
  [LimitKeys.BiometricAppLock]: 0,
  [LimitKeys.CustomLifeAreas]: 0,
  [LimitKeys.CustomizeDefaultLifeAreas]: 0,
  [LimitKeys.VisionUserPhoto]: 0,
  [LimitKeys.SubGoals]: 0,
  [LimitKeys.Insights]: 0,
  [LimitKeys.MobileCalendar1DayView]: 0,
  [LimitKeys.MobileCalendar2DayView]: 0,
  [LimitKeys.MobileCalendar3DayView]: 0,
  [LimitKeys.MobileCalendarWeekView]: 0,
  [LimitKeys.DesktopCalendarDay]: 0,
  [LimitKeys.DesktopCalendarWeek]: 0,
  [LimitKeys.DesktopCalendarFourDays]: 0,
  [LimitKeys.DesktopCalendarMonth]: 0,
  [LimitKeys.DesktopGoalsOverviewTimeline]: 0,
};

export const premiumLimitKeyMap: Record<LimitKeys, number> = {
  [LimitKeys.CreateGoal]: unlimitedLimit,
  [LimitKeys.CreateHabit]: unlimitedLimit,
  [LimitKeys.HabitIcons]: unlimitedLimit,
  [LimitKeys.HabitScheduleTarget]: unlimitedLimit,
  [LimitKeys.CreateSmartList]: unlimitedLimit,
  [LimitKeys.GoalUserPhoto]: unlimitedLimit,
  [LimitKeys.BiometricAppLock]: unlimitedLimit,
  [LimitKeys.CustomLifeAreas]: unlimitedLimit,
  [LimitKeys.CustomizeDefaultLifeAreas]: unlimitedLimit,
  [LimitKeys.VisionUserPhoto]: unlimitedLimit,
  [LimitKeys.SubGoals]: unlimitedLimit,
  [LimitKeys.Insights]: unlimitedLimit,
  [LimitKeys.MobileCalendar1DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendar2DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendar3DayView]: unlimitedLimit,
  [LimitKeys.MobileCalendarWeekView]: unlimitedLimit,
  [LimitKeys.DesktopCalendarDay]: unlimitedLimit,
  [LimitKeys.DesktopCalendarWeek]: unlimitedLimit,
  [LimitKeys.DesktopCalendarFourDays]: unlimitedLimit,
  [LimitKeys.DesktopCalendarMonth]: unlimitedLimit,
  [LimitKeys.DesktopGoalsOverviewTimeline]: unlimitedLimit,
};

export const weekdaysNumberMap: Record<WeekDays, Day> = {
  [WeekDays.Monday]: 1,
  [WeekDays.Tuesday]: 2,
  [WeekDays.Wednesday]: 3,
  [WeekDays.Thursday]: 4,
  [WeekDays.Friday]: 5,
  [WeekDays.Saturday]: 6,
  [WeekDays.Sunday]: 0,
};

export const reminderTimes = [
  9 * 60 * 60 * 1000, // 09:00
  12 * 60 * 60 * 1000, // 12:00
  15 * 60 * 60 * 1000, // 15:00
  18 * 60 * 60 * 1000, // 18:00
  21 * 60 * 60 * 1000, // 21:00
];

export const sideNavigationHoverClass = 'side-navigation-show-on-hover';

export const shortcutsLink =
  'https://griply.app/help/features/keyboard-shortcuts';
export const redditLink = 'https://www.reddit.com/r/GriplyApp/';
export const discordLink = 'https://discord.gg/4ptd9hGw5V';
export const helpCenterLink = 'https://griply.app/help';
export const changelogLink = 'https://griply.app/changelog';

export const subTaskLevel = 1;

export const durationOptions = [
  900000, // 00:15 (15 minutes)
  1800000, // 00:30 (30 minutes)
  2700000, // 00:45 (45 minutes)
  3600000, // 01:00 (1 hour)
  5400000, // 01:30 (1.5 hours)
  7200000, // 02:00 (2 hours)
  10800000, // 03:00 (3 hours)
  14400000, // 04:00 (4 hours)
];

export const defaultRruleOptions = {
  freq: Frequency.WEEKLY,
  byweekday: getRruleWeekDays(fullWeek),
};
export const defaultRrule = new RRule(defaultRruleOptions).toString();
export const defaultFrequency: ScheduleFrequency = {
  count: 1,
  unit: FrequencyUnit.Week,
  label: null,
};

export const createDefaultHabitSchedule: () => HabitSchedule = (
  startDate = new Date(),
) => ({
  frequency: null,
  rrule: { format: defaultRrule, referenceDate: RruleReferenceDate.StartDate },
  completionTarget: {
    count: 1,
    unit: CompletionTargetUnit.Day,
  },
  startDate,
  entries: [],
});

export const halfHour = 30 * 60 * 1000;

export const newTrialAllowedDate = new Date('2024-06-10T00:00:00Z');
